import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIllustration,
  hydrateIllustrationFromMetadata,
  clearIllustrationRequest,
  putClientPolicyApplicationStepRequest,
} from '@client/model';

interface UseIllustrationStepPersistenceProps {
  clientPolicyApplicationId: string;
  stepMetadata: any;
  stepStatus: PolicyApplicationStep['status'];
  hasIllustrationPdf: string | boolean | null;
  hasIllustrationError: boolean;
  isCreatingIllustration: boolean;
  buildIllustrationMetadata: () => any;
  handleSaveIllustrationError: () => void;
}

/**
 * Custom hook to handle illustration state persistence to/from policy application step.
 * Manages:
 * - Hydrating illustration from step metadata on mount
 * - Saving illustration errors to step status
 * - Saving illustration success metadata when complete
 */
export function useIllustrationStepPersistence({
  clientPolicyApplicationId,
  stepMetadata,
  stepStatus,
  hasIllustrationPdf,
  hasIllustrationError,
  isCreatingIllustration,
  buildIllustrationMetadata,
  handleSaveIllustrationError,
}: UseIllustrationStepPersistenceProps) {
  const dispatch = useDispatch();
  const illustration = useSelector(selectIllustration);
  const hasHydratedRef = useRef(false);
  const savedRequestIdRef = useRef<string | null>(null);
  const clearedIllustrationIdsRef = useRef<Set<string>>(new Set());

  // Effect 1: Hydrate illustration data from step metadata on mount
  useEffect(() => {
    const illustrationRequest = stepMetadata?.illustrationRequest;

    if (
      illustrationRequest &&
      illustrationRequest.pdfUrl &&
      illustrationRequest.insuranceQuoteId &&
      !hasHydratedRef.current
    ) {
      // Validate that the cached illustration belongs to the current policy application
      const cachedIllustration =
        illustration.completedIllustrations?.[illustrationRequest.insuranceQuoteId];
      const belongsToCurrentApplication =
        !cachedIllustration ||
        cachedIllustration.clientPolicyApplicationId === clientPolicyApplicationId;

      if (belongsToCurrentApplication && !cachedIllustration) {
        // Only hydrate if we don't already have this illustration in Redux
        hasHydratedRef.current = true;
        dispatch(
          hydrateIllustrationFromMetadata({
            insuranceQuoteId: illustrationRequest.insuranceQuoteId,
            requestId: illustrationRequest.requestId,
            pdfUrl: illustrationRequest.pdfUrl,
            premiumAmount: stepMetadata.selectedPremium,
            responseData: illustrationRequest.responseData,
            fundingSourceId: illustrationRequest.fundingSourceId,
            downloaded: illustrationRequest.downloaded,
            clientPolicyApplicationId,
          }),
        );
      } else if (
        !belongsToCurrentApplication &&
        !clearedIllustrationIdsRef.current.has(illustrationRequest.insuranceQuoteId)
      ) {
        // Clear the cached illustration if it belongs to a different policy application
        // Only clear once per insuranceQuoteId to prevent infinite loop
        console.log(
          '[useIllustrationStepPersistence] Clearing stale illustration from different policy application',
        );
        clearedIllustrationIdsRef.current.add(illustrationRequest.insuranceQuoteId);
        dispatch(
          clearIllustrationRequest({ insuranceQuoteId: illustrationRequest.insuranceQuoteId }),
        );
      }
    }
  }, [stepMetadata, illustration.completedIllustrations, dispatch, clientPolicyApplicationId]);

  // Effect 2: Save illustration error to step status (but not when skipping)
  // When user clicks "Continue Without Illustration", handleSkipStep will save the error
  useEffect(() => {
    if (
      hasIllustrationError &&
      !['error', 'completed', 'skipped'].includes(stepStatus) &&
      !isCreatingIllustration
    ) {
      handleSaveIllustrationError();
    }
  }, [hasIllustrationError, stepStatus, handleSaveIllustrationError, isCreatingIllustration]);

  // Effect 3: Save illustration metadata when illustration completes successfully
  useEffect(() => {
    if (hasIllustrationPdf && !hasIllustrationError && stepStatus !== 'completed') {
      const metadata = buildIllustrationMetadata();
      if (metadata && metadata.requestId && metadata.requestId !== savedRequestIdRef.current) {
        savedRequestIdRef.current = metadata.requestId;
        dispatch(
          putClientPolicyApplicationStepRequest({
            clientPolicyApplicationId,
            stepType: 'download-policy-illustration',
            metadata,
          }),
        );
      }
    }
  }, [
    hasIllustrationPdf,
    hasIllustrationError,
    stepStatus,
    buildIllustrationMetadata,
    dispatch,
    clientPolicyApplicationId,
  ]);

  return {
    // Return refs in case they're needed externally
    hasHydrated: hasHydratedRef.current,
    savedRequestId: savedRequestIdRef.current,
  };
}
